import React, { useState, useEffect, useCallback, Fragment } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  Chip,
  Skeleton,
  Pagination,
  Alert,
  Tooltip,
  Fade,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  RadioButtonUnchecked as UncheckedIcon,
  InfoOutlined as InfoIcon,
  MenuBook as MenuBookIcon,
  SearchOff as SearchOffIcon,
  FlagOutlined as FlagIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { dictionaryService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import SuggestWordForm from '../../components/SuggestWordForm';
import ReportCorrectionForm from '../../components/ReportCorrectionForm';
import DictionaryLanguageSidebar from '../../components/DictionaryLanguageSidebar';
import LongestWordCard from '../../components/LongestWordCard';
import PlayWordButton from '../../components/PlayWordButton';

const PAGE_SIZE = 30;

// Illustrations des entrees : /illustrations/manifest.json associe le champ
// 'duala' exact d'une entree a son image. Il est genere par
// backend/src/utils/buildIllustrationManifest.js a partir des noms de fichiers
// (convention <mot-francais>[-<mot-duala>].webp) et du mapping manuel.
let illustrationManifestPromise = null;
function loadIllustrationManifest() {
  if (!illustrationManifestPromise) {
    illustrationManifestPromise = fetch('/illustrations/manifest.json')
      .then(r => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return illustrationManifestPromise;
}

// Categories des mots illustres : /illustrations/categories.json associe un
// identifiant de categorie a la liste des champs 'duala' exacts (meme genre de
// fichier que le manifest, genere par le meme script).
let illustrationCategoriesPromise = null;
function loadIllustrationCategories() {
  if (!illustrationCategoriesPromise) {
    illustrationCategoriesPromise = fetch('/illustrations/categories.json')
      .then(r => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return illustrationCategoriesPromise;
}

const CATEGORY_LABELS = {
  fr: { animaux: 'Animaux', plantes: 'Plantes et nourriture', maison: 'Maison et objets', lieux: 'Lieux',
    vetements: 'Vêtements et parure', corps: 'Corps humain', nature: 'Nature et météo', eau: 'Eau et pêche',
    personnes: 'Personnes et métiers', actions: 'Actions', proverbes: 'Proverbes' },
  en: { animaux: 'Animals', plantes: 'Plants and food', maison: 'Home and objects', lieux: 'Places',
    vetements: 'Clothes and jewellery', corps: 'Human body', nature: 'Nature and weather', eau: 'Water and fishing',
    personnes: 'People and trades', actions: 'Actions', proverbes: 'Proverbs' },
  es: { animaux: 'Animales', plantes: 'Plantas y comida', maison: 'Casa y objetos', lieux: 'Lugares',
    vetements: 'Ropa y adornos', corps: 'Cuerpo humano', nature: 'Naturaleza y clima', eau: 'Agua y pesca',
    personnes: 'Personas y oficios', actions: 'Acciones', proverbes: 'Proverbios' },
  de: { animaux: 'Tiere', plantes: 'Pflanzen und Essen', maison: 'Haus und Gegenstände', lieux: 'Orte',
    vetements: 'Kleidung und Schmuck', corps: 'Körper', nature: 'Natur und Wetter', eau: 'Wasser und Fischfang',
    personnes: 'Menschen und Berufe', actions: 'Handlungen', proverbes: 'Sprichwörter' },
};

// Pour l'allemand, le champ 'allemand' des exemples reproduit tel quel le
// texte original de Dinkelacker 1914 : le mot duala et sa traduction
// allemande imprimes sur la meme ligne, sans separateur (voir note_generale
// des fichiers dictionnaire). On retire ici le prefixe duala (compte en
// nombre de mots, car l'orthographe exacte des diacritiques peut differer
// legerement entre la transcription moderne de 'duala' et celle d'epoque
// dans 'allemand') pour ne garder que la traduction, et reappliquer le
// meme format "duala — traduction" que les autres langues.
function germanExampleTranslation(ex) {
  const duala = (ex.duala || '').trim();
  const allemand = (ex.allemand || '').trim();
  if (!duala || !allemand) return allemand;
  const dualaWordCount = duala.split(/\s+/).length;
  const allemandWords = allemand.split(/\s+/);
  if (allemandWords.length <= dualaWordCount) return allemand;
  return allemandWords.slice(dualaWordCount).join(' ');
}

// Configuration par langue : quel champ de l'entree afficher, et les libelles
// d'interface.
const LANG_CONFIG = {
  fr: {
    mainField: 'french',
    exField: 'francais',
    title: 'Dictionnaire Duala–Français',
    placeholder: 'Chercher un mot en douala (début du mot)...',
    placeholderTranslation: 'Chercher un mot en français...',
    translationLangLabel: 'Français',
    noResultsLetter: 'Aucune entrée pour cette lettre.',
    noResultsSearch: 'Aucun résultat pour cette recherche.',
    toVerify: 'à vérifier',
    sourceNoteBefore: 'Basé sur le ',
    sourceNoteAfter: " d'E. Dinkelacker (1914, domaine public). Certaines entrées n'ont pas encore été relues par un locuteur natif — elles sont marquées ",
    quoteOpen: '« ',
    quoteClose: ' »',
    noteLabel: 'Note',
    suggestLinkLabel: 'Vous ne trouvez pas un mot ? Suggérez-le',
    illustratedOnlyLabel: "Mots illustrés seulement",
    unverifiedOnlyLabel: "À vérifier seulement",
    allVerifiedHere: "Tout est vérifié ici. Bravo !",
    entriesLabel: (n) => `${n} entrée${n > 1 ? 's' : ''}`,
    helpText: "Quand un mot est suivi d'un autre mot entre parenthèses avec un petit chiffre " +
      '(ex. anda²), cela signifie qu\'il dérive de ce mot racine, précisément du sens n°2 de ' +
      'celui-ci (le dictionnaire source numérote les mots qui se prononcent pareil mais ont des ' +
      'sens différents). Survolez la parenthèse pour voir l\'explication.',
  },
  en: {
    mainField: 'english',
    exField: 'anglais',
    noteField: 'note_english',
    title: 'Duala–English Dictionary',
    placeholder: 'Search a Duala word (start of the word)...',
    placeholderTranslation: 'Search a word in English...',
    translationLangLabel: 'English',
    noResultsLetter: 'No entries for this letter.',
    noResultsSearch: 'No results for this search.',
    toVerify: 'to verify',
    sourceNoteBefore: "Based on E. Dinkelacker's ",
    sourceNoteAfter: ' (1914, public domain). Some entries have not yet been reviewed by a native speaker — they are marked ',
    quoteOpen: '“',
    quoteClose: '”',
    noteLabel: 'Note',
    fallbackNote: 'English translation not available yet — showing French',
    suggestLinkLabel: "Can't find a word? Suggest it",
    illustratedOnlyLabel: "Illustrated words only",
    unverifiedOnlyLabel: "To verify only",
    allVerifiedHere: "Everything is verified here. Well done!",
    entriesLabel: (n) => `${n} entr${n > 1 ? 'ies' : 'y'}`,
    helpText: 'When a word is followed by another word in parentheses with a small digit ' +
      '(e.g. anda²), it means it derives from that root word, specifically sense n°2 of it ' +
      '(the source dictionary numbers words that sound alike but have different meanings). ' +
      'Hover the parenthesis to see the explanation.',
  },
  es: {
    mainField: 'spanish',
    exField: 'espagnol',
    noteField: 'note_spanish',
    title: 'Diccionario Duala–Español',
    placeholder: 'Buscar una palabra duala (inicio de la palabra)...',
    placeholderTranslation: 'Buscar una palabra en español...',
    translationLangLabel: 'Español',
    noResultsLetter: 'No hay entradas para esta letra.',
    noResultsSearch: 'Sin resultados para esta búsqueda.',
    toVerify: 'por verificar',
    sourceNoteBefore: 'Basado en el ',
    sourceNoteAfter: ' de E. Dinkelacker (1914, dominio público). Algunas entradas aún no han sido revisadas por un hablante nativo — están marcadas ',
    quoteOpen: '«',
    quoteClose: '»',
    noteLabel: 'Nota',
    fallbackNote: 'Traducción al español aún no disponible — mostrando francés',
    suggestLinkLabel: '¿No encuentras una palabra? Sugiérela',
    illustratedOnlyLabel: "Solo palabras ilustradas",
    unverifiedOnlyLabel: "Solo por verificar",
    allVerifiedHere: "Todo está verificado aquí. ¡Bien!",
    entriesLabel: (n) => `${n} entrada${n > 1 ? 's' : ''}`,
    helpText: 'Cuando una palabra va seguida de otra entre paréntesis con un pequeño número ' +
      '(ej. anda²), significa que deriva de esa palabra raíz, precisamente del sentido n°2 de ' +
      'esta (el diccionario fuente numera las palabras que suenan igual pero tienen sentidos ' +
      'diferentes). Pase el cursor sobre el paréntesis para ver la explicación.',
  },
  de: {
    mainField: 'german',
    exField: 'allemand',
    noteField: 'note_german',
    // Contrairement a fr/en/es, le champ 'allemand' des exemples inclut deja
    // le mot duala au debut (texte original de Dinkelacker 1914, voir
    // note_generale des fichiers dictionnaire) : germanExampleTranslation()
    // le retire pour eviter un doublon visuel avec ex.duala.
    exampleIncludesDuala: true,
    title: 'Duala–Deutsch Wörterbuch',
    placeholder: 'Ein Duala-Wort suchen (Wortanfang)...',
    placeholderTranslation: 'Ein deutsches Wort suchen...',
    translationLangLabel: 'Deutsch',
    noResultsLetter: 'Keine Einträge für diesen Buchstaben.',
    noResultsSearch: 'Keine Ergebnisse für diese Suche.',
    toVerify: 'zu prüfen',
    sourceNoteBefore: 'Basierend auf dem ',
    sourceNoteAfter: ' von E. Dinkelacker (1914, gemeinfrei). Manche Einträge wurden noch nicht von einem Muttersprachler überprüft — sie sind mit ',
    quoteOpen: '„',
    quoteClose: '“',
    noteLabel: 'Anmerkung',
    fallbackNote: 'Deutsche Übersetzung noch nicht verfügbar — Französisch wird angezeigt',
    suggestLinkLabel: 'Wort nicht gefunden? Schlagen Sie es vor',
    illustratedOnlyLabel: "Nur Wörter mit Bild",
    unverifiedOnlyLabel: "Nur zu prüfen",
    allVerifiedHere: "Hier ist alles geprüft. Gut gemacht!",
    entriesLabel: (n) => `${n} Eintr${n > 1 ? 'äge' : 'ag'}`,
    helpText: 'Wenn auf ein Wort ein weiteres Wort in Klammern mit einer kleinen Ziffer folgt ' +
      '(z. B. anda²), bedeutet dies, dass es von diesem Wurzelwort abgeleitet ist, genauer von ' +
      'dessen Bedeutung Nr. 2 (das Quellwörterbuch nummeriert gleich klingende Wörter mit ' +
      'unterschiedlicher Bedeutung). Fahren Sie über die Klammer, um die Erklärung zu sehen.',
  },
};

// Repere les renvois du style "(anda2)" dans un mot douala : le chiffre
// distingue quel sens numerote du mot racine "anda" est concerne (le
// dictionnaire source numerote les homonymes 1., 2., etc.).
const ROOT_REF_RE = /\(([a-zA-ZàâäéèêëïîôöùûüçñáéíóúÁÉÍÓÚñÑɛ ɔ'-]+?)([0-9])\)/g;

function renderDuala(text) {
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  ROOT_REF_RE.lastIndex = 0;
  while ((match = ROOT_REF_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(<Fragment key={key++}>{text.slice(lastIndex, match.index)}</Fragment>);
    }
    const [, root, digit] = match;
    parts.push(
      <Tooltip
        key={key++}
        title={`Dérivé du mot racine « ${root} », sens n°${digit} dans le dictionnaire source`}
        arrow
      >
        <Box component="span" sx={{ cursor: 'help' }}>
          ({root}<Box component="sup" sx={{ fontSize: '0.7em' }}>{digit}</Box>)
        </Box>
      </Tooltip>
    );
    lastIndex = ROOT_REF_RE.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(<Fragment key={key++}>{text.slice(lastIndex)}</Fragment>);
  }
  return parts;
}

// Met en évidence la partie du texte qui a matché la recherche — utile en mode traduction,
// où le mot cherché peut apparaître au milieu d'une définition plus longue plutôt qu'au debut
// (ex. "un petit filet" pour une recherche "petit") : sans ça, pas evident de voir pourquoi
// une entree est remontee.
function highlightMatch(text, query) {
  const trimmed = query.trim();
  if (!trimmed || trimmed.length < 2 || typeof text !== 'string') return text;
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`(${escaped})`, 'gi');
  const parts = text.split(re);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <Box
        component="mark"
        key={i}
        sx={{ bgcolor: 'warning.light', color: 'text.primary', px: 0.3, borderRadius: 0.5 }}
      >
        {part}
      </Box>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    )
  );
}

// Tri "naturel" des onglets de lettres : localeCompare place Ñ juste après N
// au lieu de le rejeter en toute fin de liste (comparaison ASCII brute côté API).
function sortLetters(letters) {
  return [...letters].sort((a, b) => a.letter.localeCompare(b.letter, 'fr'));
}

const LETTER_LABEL = { fr: 'Lettre', en: 'Letter', es: 'Letra', de: 'Buchstabe' };

// Sur mobile la grille de lettres est repliée par défaut (et se referme après
// un choix) pour laisser la place aux entrées ; sur grand écran elle reste ouverte.
function LetterNav({ letters, activeLetter, onChange, language }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  const [open, setOpen] = useState(!isMobile);

  const handlePick = (letter) => {
    onChange(letter);
    if (isMobile) setOpen(false);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        endIcon={
          <ExpandMoreIcon
            sx={{ transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }}
          />
        }
        sx={{ textTransform: 'none', fontWeight: 600, px: 1, mb: open ? 1 : 0 }}
      >
        {LETTER_LABEL[language] || LETTER_LABEL.fr}{activeLetter ? ` ${activeLetter}` : ''}
      </Button>
      <Collapse in={open} unmountOnExit>
        <LetterGrid letters={letters} activeLetter={activeLetter} onChange={handlePick} />
      </Collapse>
    </Box>
  );
}

function LetterGrid({ letters, activeLetter, onChange }) {
  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {sortLetters(letters).map((l) => {
        const active = l.letter === activeLetter;
        return (
          <Tooltip key={l.letter} title={`${l.count} mots`} arrow>
            <Box
              component="button"
              onClick={() => onChange(l.letter)}
              sx={{
                minWidth: 40,
                height: 40,
                px: 1,
                borderRadius: '10px',
                border: '1px solid',
                borderColor: active ? 'primary.main' : 'divider',
                bgcolor: active ? 'primary.main' : 'background.paper',
                color: active ? 'primary.contrastText' : 'text.primary',
                fontWeight: active ? 700 : 500,
                fontSize: '0.95rem',
                fontFamily: 'inherit',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                boxShadow: active ? '0 2px 8px rgba(25,118,210,0.35)' : 'none',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: active ? 'primary.dark' : 'primary.light',
                  color: active ? 'primary.contrastText' : 'primary.contrastText',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              {l.letter}
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
}

function EntrySkeleton() {
  return (
    <Stack spacing={2}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Paper key={i} variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
          <Skeleton variant="text" width="30%" height={32} />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="45%" />
        </Paper>
      ))}
    </Stack>
  );
}

function Dictionary() {
  const { isAuthenticated, user } = useAuth();
  const isStaff = !!user && ['admin', 'super_admin', 'relecteur'].includes(user.role);
  const [language, setLanguage] = useState('fr');
  const [letters, setLetters] = useState([]);
  const [activeLetter, setActiveLetter] = useState(null);
  const [query, setQuery] = useState('');
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [correctionEntryId, setCorrectionEntryId] = useState(null);
  const [expandedImageEntryId, setExpandedImageEntryId] = useState(null);
  const [illustrations, setIllustrations] = useState({});
  // Mode "mots illustres seulement" : liste a plat de la selection, sans choix de lettre
  const [illustratedOnly, setIllustratedOnly] = useState(false);
  // Equipe seulement : n'afficher que les mots a verifier (filtre applique cote serveur)
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);
  const unverifiedFlag = isStaff && unverifiedOnly ? 1 : undefined;
  // Categorie choisie dans la selection illustree (null = toutes)
  const [categories, setCategories] = useState({});
  const [category, setCategory] = useState(null);

  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    loadIllustrationManifest().then(setIllustrations);
    loadIllustrationCategories().then(setCategories);
  }, []);
  // 'duala' (prefixe, colonne duala) ou 'translation' (sous-chaine, colonne de traduction
  // choisie via 'language') : deux modes explicites plutot qu'une recherche bilingue melangee
  // par defaut (qui remontait des mots sans rapport, voir commit f85c0f1).
  const [searchField, setSearchField] = useState('duala');

  const lang = LANG_CONFIG[language];

  useEffect(() => {
    dictionaryService.getLetters().then(res => {
      const ls = res.data.letters;
      setLetters(ls);
      if (ls.length > 0) setActiveLetter(ls[0].letter);
    }).catch(err => console.error('Erreur chargement lettres:', err));
  }, []);

  const loadByLetter = useCallback((letter, p, unverified) => {
    setLoading(true);
    dictionaryService.getByLetter(letter, { page: p, limit: PAGE_SIZE, unverified })
      .then(res => {
        setEntries(res.data.entries);
        setTotal(res.data.total);
      })
      .catch(err => console.error('Erreur chargement entrées:', err))
      .finally(() => setLoading(false));
  }, []);

  const loadIllustrated = useCallback((p, q, searchLang, field, cat, unverified) => {
    const words = cat && categories[cat] ? categories[cat] : Object.keys(illustrations);
    if (words.length === 0) return;
    setLoading(true);
    const searching = q.length >= 2;
    const searchParams = !searching ? {}
      : field === 'translation'
        ? { q, lang: searchLang, field: 'translation' }
        : { q, lang: searchLang, field: 'duala', mode: 'prefix' };
    dictionaryService.getIllustrated({ words, page: p, limit: PAGE_SIZE, unverified, ...searchParams })
      .then(res => {
        setEntries(res.data.entries);
        setTotal(res.data.total);
      })
      .catch(err => console.error('Erreur chargement mots illustrés:', err))
      .finally(() => setLoading(false));
  }, [illustrations, categories]);

  const runSearch = useCallback((q, p, searchLang, field, unverified) => {
    setLoading(true);
    // field=duala + mode=prefix : chercher un mot duala qui commence par ce qui est tape.
    // field=translation (sans mode, donc sous-chaine) : chercher dans la traduction choisie.
    // Jamais les deux mélangés par defaut (remontait des mots sans rapport, voir commit f85c0f1).
    const params = field === 'translation'
      ? { page: p, limit: PAGE_SIZE, lang: searchLang, field: 'translation', unverified }
      : { page: p, limit: PAGE_SIZE, lang: searchLang, field: 'duala', mode: 'prefix', unverified };
    dictionaryService.search(q, params)
      .then(res => {
        setEntries(res.data.entries);
        setTotal(res.data.total);
      })
      .catch(err => console.error('Erreur recherche:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (illustratedOnly) {
      const q = query.trim();
      const t = setTimeout(() => loadIllustrated(page, q, language, searchField, category, unverifiedFlag), q.length >= 2 ? 350 : 0);
      return () => clearTimeout(t);
    }
    if (query.trim().length >= 2) {
      const t = setTimeout(() => runSearch(query.trim(), 1, language, searchField, unverifiedFlag), 350);
      setPage(1);
      return () => clearTimeout(t);
    } else if (query.trim().length === 0 && activeLetter) {
      loadByLetter(activeLetter, page, unverifiedFlag);
    }
  }, [query, activeLetter, runSearch, loadByLetter, loadIllustrated, illustratedOnly, category, page, language, searchField, unverifiedFlag]);

  const handleLanguageChange = (code) => {
    setLanguage(code);
    setPage(1);
    if (illustratedOnly) return; // l'effet recharge la liste
    if (query.trim().length >= 2) {
      runSearch(query.trim(), 1, code, searchField, unverifiedFlag);
    }
  };

  const handleSearchFieldChange = (_, value) => {
    if (!value) return;
    setSearchField(value);
    setPage(1);
    if (illustratedOnly) return; // l'effet recharge la liste
    if (query.trim().length >= 2) {
      runSearch(query.trim(), 1, language, value, unverifiedFlag);
    }
  };

  const handleUnverifiedToggle = () => {
    setUnverifiedOnly((v) => !v);
    setPage(1);
  };

  const handleIllustratedToggle = () => {
    setIllustratedOnly((v) => !v);
    setCategory(null);
    setPage(1);
    setEntries([]);
    setTotal(0);
  };

  const handleCategoryChange = (id) => {
    setCategory((current) => (current === id ? null : id));
    setPage(1);
  };

  const handleLetterChange = (newLetter) => {
    setActiveLetter(newLetter);
    setQuery('');
    setPage(1);
    loadByLetter(newLetter, 1);
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    if (illustratedOnly) {
      // l'effet recharge la liste
    } else if (query.trim().length >= 2) {
      runSearch(query.trim(), newPage, language, searchField);
    } else if (activeLetter) {
      loadByLetter(activeLetter, newPage);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleVerified = (entry) => {
    const next = !entry.verified;
    setEntries(prev => prev.map(e => (e.id === entry.id ? { ...e, verified: next } : e)));
    dictionaryService.setVerified(entry.id, next).catch(err => {
      console.error('Erreur maj vérifié:', err);
      setEntries(prev => prev.map(e => (e.id === entry.id ? { ...e, verified: entry.verified } : e)));
    });
  };

  const isSearching = query.trim().length >= 2;

  return (
    <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
      <DictionaryLanguageSidebar language={language} onChange={handleLanguageChange} />
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 4 },
          flex: 1,
          minWidth: 0,
          borderRadius: 3,
          background: 'linear-gradient(180deg, rgba(25,118,210,0.04) 0%, rgba(25,118,210,0) 140px)',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              flexShrink: 0,
            }}
          >
            <MenuBookIcon />
          </Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: 'primary.dark', lineHeight: 1.15 }}>
            {lang.title}
          </Typography>
          <Chip
            label={language === 'fr' ? 'Bêta' : 'Beta'}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ fontWeight: 700, height: 22 }}
          />
          <Tooltip title={lang.helpText} arrow placement="right">
            <IconButton size="small" sx={{ color: 'text.disabled' }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: { sm: '58px' } }}>
          {lang.sourceNoteBefore}<em>Wörterbuch der Duala-Sprache</em>{lang.sourceNoteAfter}
          {lang.quoteOpen}{lang.toVerify}{lang.quoteClose}.
        </Typography>

        <LongestWordCard language={language} />

        <TextField
          fullWidth
          placeholder={searchField === 'translation' ? lang.placeholderTranslation : lang.placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: query && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setQuery('')} aria-label="Effacer la recherche">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              bgcolor: 'background.paper',
              fontSize: '1.05rem',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
              },
            },
          }}
        />

        <ToggleButtonGroup
          value={searchField}
          exclusive
          onChange={handleSearchFieldChange}
          size="small"
          sx={{
            mb: 3,
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              borderRadius: 2,
            },
          }}
        >
          <ToggleButton value="duala">Duala</ToggleButton>
          <ToggleButton value="translation">{lang.translationLangLabel}</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ mb: 3 }}>
          <Button
            size="small"
            onClick={() => setSuggestOpen((v) => !v)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {suggestOpen ? '×' : '+'} {lang.suggestLinkLabel}
          </Button>
          {suggestOpen && <SuggestWordForm defaultWord={query.trim()} />}
        </Box>

        <Box sx={{ mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Orange tant que le filtre est inactif (attire l'oeil), vert une fois actif. */}
          <Button
            variant="contained"
            startIcon={illustratedOnly ? <CheckCircleIcon /> : <ImageIcon />}
            onClick={handleIllustratedToggle}
            disabled={Object.keys(illustrations).length === 0}
            aria-pressed={illustratedOnly}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1rem',
              borderRadius: 3,
              px: 2.5,
              py: 1,
              color: '#fff',
              bgcolor: illustratedOnly ? '#2e7d32' : '#c2410c',
              boxShadow: 3,
              '&:hover': { bgcolor: illustratedOnly ? '#1b5e20' : '#9a3412' },
            }}
          >
            {lang.illustratedOnlyLabel}
          </Button>
          {isStaff && (
            <Button
              variant={unverifiedOnly ? 'contained' : 'outlined'}
              color="warning"
              startIcon={unverifiedOnly ? <CheckCircleIcon /> : <PendingIcon />}
              onClick={handleUnverifiedToggle}
              aria-pressed={unverifiedOnly}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: '1rem', borderRadius: 3, px: 2.5, py: 1 }}
            >
              {lang.unverifiedOnlyLabel}
            </Button>
          )}
        </Box>

        {illustratedOnly && Object.keys(categories).length > 0 && (
          // Une seule ligne qui defile sur telephone : onze pastilles sur plusieurs lignes
          // mangeraient l'ecran.
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mb: 2,
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              overflowX: { xs: 'auto', sm: 'visible' },
              pb: { xs: 0.5, sm: 0 },
            }}
          >
            {Object.keys(categories).map((id) => (
              <Chip
                key={id}
                label={(CATEGORY_LABELS[language] || CATEGORY_LABELS.fr)[id] || id}
                size="small"
                color={category === id ? 'primary' : 'default'}
                variant={category === id ? 'filled' : 'outlined'}
                onClick={() => handleCategoryChange(id)}
                aria-pressed={category === id}
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Box>
        )}

        {!isSearching && !illustratedOnly && letters.length > 0 && (
          <LetterNav letters={letters} activeLetter={activeLetter} onChange={handleLetterChange} language={language} />
        )}

        {!loading && entries.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontWeight: 600, letterSpacing: 0.3 }}>
            {lang.entriesLabel(total)}
            {!isSearching && !illustratedOnly && activeLetter ? ` — lettre ${activeLetter}` : ''}
          </Typography>
        )}

        {loading ? (
          <EntrySkeleton />
        ) : entries.length === 0 ? (
          <>
            <Alert icon={<SearchOffIcon />} severity="info" sx={{ borderRadius: 2 }}>
              {unverifiedFlag ? lang.allVerifiedHere : isSearching ? lang.noResultsSearch : lang.noResultsLetter}
            </Alert>
            {isSearching && <SuggestWordForm defaultWord={query.trim()} />}
          </>
        ) : (
          <>
            <List disablePadding>
              {entries.map((entry, i) => {
                const mainText = entry[lang.mainField];
                const usesFallback = language !== 'fr' && !mainText;
                return (
                  <Fade in key={entry.id} timeout={300} style={{ transitionDelay: `${Math.min(i, 8) * 30}ms` }}>
                    <ListItem
                      disablePadding
                      sx={{
                        display: 'block',
                        mb: 1.5,
                      }}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderRadius: 3,
                          borderColor: 'divider',
                          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
                            borderColor: 'primary.light',
                          },
                        }}
                      >
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        {illustrations[entry.duala] && (
                          <Box
                            component="img"
                            src={illustrations[entry.duala]}
                            alt=""
                            onClick={() => setExpandedImageEntryId(
                              expandedImageEntryId === entry.id ? null : entry.id
                            )}
                            sx={{
                              width: expandedImageEntryId === entry.id ? { xs: '100%', sm: 320 } : { xs: '100%', sm: 110 },
                              height: expandedImageEntryId === entry.id ? 320 : 110,
                              objectFit: 'cover',
                              borderRadius: 2,
                              flexShrink: 0,
                              cursor: 'pointer',
                              transition: 'width 0.2s ease, height 0.2s ease',
                            }}
                          />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                          <Stack direction="row" alignItems="baseline" flexWrap="wrap" gap={1}>
                            <Typography
                              variant="h6"
                              component="span"
                              sx={{ fontStyle: 'italic', fontWeight: 700, color: 'primary.dark' }}
                            >
                              {renderDuala(entry.duala)}
                            </Typography>
                            {entry.grammar && (
                              <Chip
                                label={entry.grammar}
                                size="small"
                                variant="outlined"
                                sx={{ fontStyle: 'italic', height: 22, fontSize: '0.75rem', color: 'text.secondary' }}
                              />
                            )}
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                            <PlayWordButton file={entry.audio} language={language} />
                            {!entry.verified && (
                              <Chip label={lang.toVerify} size="small" color="warning" variant="outlined" />
                            )}
                            {isAuthenticated && (
                              <Tooltip title="Signaler une erreur sur cette entrée">
                                <IconButton
                                  size="small"
                                  onClick={() => setCorrectionEntryId(correctionEntryId === entry.id ? null : entry.id)}
                                >
                                  <FlagIcon fontSize="small" color={correctionEntryId === entry.id ? 'primary' : 'disabled'} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isAuthenticated && (
                              <Tooltip title={entry.verified ? 'Marquer comme non vérifié' : 'Marquer comme vérifié'}>
                                <IconButton size="small" onClick={() => toggleVerified(entry)}>
                                  {entry.verified
                                    ? <CheckCircleIcon fontSize="small" color="success" />
                                    : <UncheckedIcon fontSize="small" color="disabled" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>

                        <Typography variant="body1" sx={{ mt: 0.75 }}>
                          {isSearching && searchField === 'translation'
                            ? highlightMatch(mainText || entry.french, query)
                            : (mainText || entry.french)}
                        </Typography>
                        {usesFallback && (
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                            {lang.fallbackNote}
                          </Typography>
                        )}

                        {entry.examples && entry.examples.length > 0 && (
                          <Box
                            sx={{
                              mt: 1.5,
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: 'rgba(25,118,210,0.05)',
                              borderLeft: '3px solid',
                              borderColor: 'primary.light',
                            }}
                          >
                            {entry.examples.map((ex, exi) => (
                              <Typography
                                key={exi}
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontStyle: 'italic', '&:not(:last-child)': { mb: 0.5 } }}
                              >
                                {renderDuala(ex.duala)} — {lang.exampleIncludesDuala
                                  ? germanExampleTranslation(ex)
                                  : (ex[lang.exField] || ex.francais)}
                              </Typography>
                            ))}
                          </Box>
                        )}

                        {entry.note && (
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
                            {lang.noteLabel} : {(lang.noteField && entry[lang.noteField]) || entry.note}
                          </Typography>
                        )}

                        {isStaff && entry.internal_note && (
                          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                            Note interne (équipe uniquement) : {entry.internal_note}
                          </Typography>
                        )}

                        {correctionEntryId === entry.id && (
                          <ReportCorrectionForm entry={entry} onClose={() => setCorrectionEntryId(null)} />
                        )}
                        </Box>
                      </Stack>
                      </Paper>
                    </ListItem>
                  </Fade>
                );
              })}
            </List>

            {total > PAGE_SIZE && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(total / PAGE_SIZE)}
                  page={page}
                  onChange={handlePageChange}
                  shape="rounded"
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}

export default Dictionary;
